import { fabric } from 'fabric';

import { loadFont } from './fonts';
import { fitTextWidth, makeLine, makePill, makeText } from './objects';
import type { ArtboardSize, BrandInfo, FontPair, Translate } from './types';

/**
 * Text presets and combos. Sizes are fractions of the artboard so a headline
 * reads the same on a square post and a story. Combos (eyebrow + headline,
 * title + rule + caption) are separate layers added as one selection: they
 * move together on arrival but each stays directly editable.
 */
export type TextPresetId =
    | 'headline'
    | 'subheading'
    | 'body'
    | 'caption'
    | 'lockup'
    | 'collection'
    | 'quote'
    | 'spec'
    | 'price'
    | 'cta'
    | 'number';

export const TEXT_STYLES: TextPresetId[] = ['headline', 'subheading', 'body', 'caption'];
export const TEXT_COMBOS: TextPresetId[] = ['lockup', 'collection', 'quote', 'spec', 'price', 'cta', 'number'];

export interface PresetContext {
    art: ArtboardSize;
    brand: BrandInfo;
    pair: FontPair;
    t: Translate;
}

/** Heading face: the brand's if it has one, otherwise the pair's. */
export const headingFont = (ctx: { brand: BrandInfo; pair: FontPair }, useBrand = true) =>
    (useBrand && ctx.brand.font) || ctx.pair.heading;

export async function buildTextPreset(id: TextPresetId, ctx: PresetContext): Promise<fabric.Object[]> {
    const { art, brand, pair, t } = ctx;
    const W = art.w;
    const u = Math.min(art.w, art.h);
    const head = headingFont(ctx);
    const body = pair.body;
    await Promise.all([
        loadFont(head, pair.headingWeight),
        loadFont(head, pair.headingWeight, true),
        loadFont(body, 400),
        loadFont(body, 500),
    ]);
    const boxW = W * 0.72;
    const x = (W - boxW) / 2;
    const cy = art.h / 2;
    const ink = brand.ink;

    switch (id) {
        case 'headline':
            return [makeText(t('DESIGN_EDITOR.PRESET_HEADLINE_TEXT'), {
                x, y: cy - u * 0.06, w: boxW, size: u * 0.085, font: head, weight: pair.headingWeight,
                color: ink, align: 'center', lineHeight: 1.05, spacing: -10, upper: pair.headingUpper,
                fontRole: 'heading', colorRole: 'ink', name: 'Headline',
            })];
        case 'subheading':
            return [makeText(t('DESIGN_EDITOR.PRESET_SUBHEADING_TEXT'), {
                x, y: cy - u * 0.025, w: boxW, size: u * 0.04, font: body, weight: 500,
                color: ink, align: 'center', lineHeight: 1.2, fontRole: 'body', colorRole: 'ink', name: 'Subheading',
            })];
        case 'body':
            return [makeText(t('DESIGN_EDITOR.PRESET_BODY_TEXT'), {
                x: (W - W * 0.6) / 2, y: cy - u * 0.04, w: W * 0.6, size: u * 0.026, font: body, weight: 400,
                color: ink, align: 'center', lineHeight: 1.45, fontRole: 'body', colorRole: 'ink', name: 'Body',
            })];
        case 'caption':
            return [makeText(t('DESIGN_EDITOR.PRESET_CAPTION_TEXT'), {
                x, y: cy, w: boxW, size: u * 0.018, font: body, weight: 400, color: ink, opacity: 0.72,
                align: 'center', spacing: 40, fontRole: 'body', colorRole: 'ink', name: 'Caption',
            })];
        case 'lockup': {
            const eyebrow = makeText(t('DESIGN_EDITOR.PRESET_EYEBROW_TEXT'), {
                x, y: cy - u * 0.09, w: boxW, size: u * 0.018, font: body, weight: 500, color: ink,
                align: 'center', spacing: 320, upper: true, fontRole: 'body', colorRole: 'ink', name: 'Eyebrow',
            });
            const headline = makeText(t('DESIGN_EDITOR.PRESET_LOCKUP_TEXT'), {
                x, y: (eyebrow.top || 0) + (eyebrow.height || 0) + u * 0.028, w: boxW, size: u * 0.075,
                font: head, weight: pair.headingWeight, color: ink, align: 'center', lineHeight: 1.05,
                upper: pair.headingUpper, fontRole: 'heading', colorRole: 'ink', name: 'Headline',
            });
            return [eyebrow, headline];
        }
        case 'collection': {
            const title = makeText(t('DESIGN_EDITOR.PRESET_COLLECTION_TEXT'), {
                x, y: cy - u * 0.08, w: boxW, size: u * 0.095, font: head, weight: pair.headingWeight,
                italic: !pair.headingUpper, color: ink, align: 'center', lineHeight: 1,
                upper: pair.headingUpper, fontRole: 'heading', colorRole: 'ink', name: 'Collection title',
            });
            const ruleY = (title.top || 0) + (title.height || 0) + u * 0.03;
            const rule = makeLine(W / 2 - u * 0.04, ruleY, W / 2 + u * 0.04, ruleY, { color: ink, width: Math.max(1.5, u * 0.002), colorRole: 'ink' });
            const sub = makeText(t('DESIGN_EDITOR.PRESET_COLLECTION_SUB'), {
                x, y: ruleY + u * 0.03, w: boxW, size: u * 0.017, font: body, weight: 500, color: ink,
                align: 'center', spacing: 360, upper: true, fontRole: 'body', colorRole: 'ink', name: 'Caption',
            });
            return [title, rule, sub];
        }
        case 'quote': {
            const q = makeText(t('DESIGN_EDITOR.PRESET_QUOTE_TEXT'), {
                x, y: cy - u * 0.08, w: boxW, size: u * 0.05, font: head, weight: pair.headingWeight,
                italic: true, color: ink, align: 'center', lineHeight: 1.18, fontRole: 'heading', colorRole: 'ink', name: 'Quote',
            });
            const by = makeText(t('DESIGN_EDITOR.PRESET_QUOTE_BY'), {
                x, y: (q.top || 0) + (q.height || 0) + u * 0.035, w: boxW, size: u * 0.017, font: body, weight: 500,
                color: ink, align: 'center', spacing: 260, upper: true, fontRole: 'body', colorRole: 'ink', name: 'Attribution',
            });
            return [q, by];
        }
        case 'spec': {
            const pill = makePill(t('DESIGN_EDITOR.PRESET_SPEC_TEXT'), {
                x: 0, y: cy, w: W, size: u * 0.022, font: body, weight: 500, color: ink, align: 'center',
                spacing: 80, bg: '#FFFFFF', padX: u * 0.024, padY: u * 0.012, radius: u * 0.05,
                fontRole: 'body', colorRole: 'ink', name: 'Spec tag',
            });
            pill.set({ left: (W - (pill.width || 0)) / 2 });
            return [pill];
        }
        case 'price': {
            const pill = makePill(t('DESIGN_EDITOR.PRESET_PRICE_TEXT'), {
                x: 0, y: cy, w: W, size: u * 0.03, font: body, weight: 500, color: brand.onAccent, align: 'center',
                bg: brand.accent, padX: u * 0.03, padY: u * 0.016, radius: u * 0.006,
                fontRole: 'body', colorRole: 'onAccent', name: 'Price tag',
            });
            pill.set({ left: (W - (pill.width || 0)) / 2 });
            return [pill];
        }
        case 'cta': {
            const pill = makePill(t('DESIGN_EDITOR.PRESET_CTA_TEXT'), {
                x: 0, y: cy, w: W, size: u * 0.024, font: body, weight: 500, color: brand.onAccent, align: 'center',
                spacing: 40, bg: brand.accent, padX: u * 0.04, padY: u * 0.02, radius: u * 0.06,
                fontRole: 'body', colorRole: 'onAccent', name: 'Button',
            });
            pill.set({ left: (W - (pill.width || 0)) / 2 });
            return [pill];
        }
        case 'number':
        default: {
            const n = makeText('01', {
                x, y: cy - u * 0.1, w: boxW, size: u * 0.16, font: head, weight: pair.headingWeight,
                color: brand.accent, align: 'center', lineHeight: 1, fontRole: 'heading', colorRole: 'accent', name: 'Number',
            });
            fitTextWidth(n);
            n.set({ left: (W - (n.width || 0)) / 2 });
            return [n];
        }
    }
}
